import React, { useState, useRef, useMemo, useEffect } from "react";
import { motion } from "framer-motion";
import { MenuItem, Menu, Kitchen } from "../../types/index";
import { useQuery } from "@tanstack/react-query";
import { getMenuByKitchenId } from "../../services/kitchen.service";
import { MainDial } from "../home/MainDial";
import { saveUserPreference } from "../../services/user.service";
import { useAuth } from "../../context/AuthProvider";
import { toast } from "react-hot-toast";
import { SavedPreference } from "../../types/user";

// --- TUNER CONFIGURATION ---
const STAGE_CONFIGS: Record<string, any> = {
  kitchen: { radii: { C1: 181, C2: 126, C3: 107, C4: 0 }, iconSizes: { full: 71, small: 36, bubble: 11 }, offset: { x: 2, y: -23 } },
  category: { radii: { C1: 181, C2: 126, C3: 107, C4: 0 }, iconSizes: { full: 54, small: 36, bubble: 11 }, offset: { x: 2, y: -23 } },
  dish: { radii: { C1: 196, C2: 163, C3: 107, C4: 0 }, iconSizes: { full: 54, small: 50, bubble: 11 }, offset: { x: 2, y: -23 } },
  variant: { radii: { C1: 226, C2: 170, C3: 137, C4: 81, C5: 181, C6: 289, C7: 304, C8: 296 }, iconSizes: { full: 72, small: 37, bubble: 11 }, offset: { x: 2, y: -23 } },
  customize: { radii: { C1: 226, C2: 170, C3: 137, C4: 81, C5: 181, C6: 289, C7: 304, C8: 296 }, iconSizes: { full: 72, small: 37, bubble: 11 }, offset: { x: 2, y: -23 } },
  addons: { radii: { C1: 181, C2: 152, C3: 107, C4: 67, C5: 200, C6: 322, C7: 311, C8: 389 }, iconSizes: { full: 72, small: 37, bubble: 11 }, offset: { x: -1, y: -23 } }
};

const DEFAULT_CONFIG = STAGE_CONFIGS.variant;
const BUTTON_POS = { bottom: 81, side: 100 };

type Step = 'kitchen' | 'category' | 'dish' | 'variant' | 'customize' | 'addons' | 'quantity' | 'summary';
type VisualMode = 'full' | 'small' | 'bubble' | 'off';
interface OrbitState { radius: number; mode: VisualMode; zIndex: number; ring: string; }

const getOrbitConfig = (step: Step): { states: Record<string, OrbitState>, currentConfig: any } => {
  const cfg = STAGE_CONFIGS[step] || DEFAULT_CONFIG;
  const r = cfg.radii;
  const c = (radius: number, mode: VisualMode, zIndex: number, ring: string): OrbitState => ({ radius, mode, zIndex, ring });
  
  let states: Record<string, OrbitState> = {};
  switch (step) {
    case 'kitchen': states = { kitchen: c(r.C1, 'full', 10, 'C1') }; break;
    case 'category': states = { kitchen: c(r.C2, 'small', 5, 'C2'), category: c(r.C1, 'full', 10, 'C1') }; break;
    case 'dish': states = { kitchen: c(r.C3, 'small', 4, 'C3'), category: c(r.C2, 'bubble', 5, 'C2'), dish: c(r.C1, 'full', 10, 'C1') }; break;
    case 'variant': states = { kitchen: c(r.C4, 'small', 3, 'C4'), category: c(r.C3, 'bubble', 4, 'C3'), dish: c(r.C2, 'small', 5, 'C2'), variant: c(r.C1, 'full', 10, 'C1') }; break;
    case 'customize': states = { kitchen: c(r.C4, 'small', 3, 'C4'), category: c(r.C3, 'bubble', 4, 'C3'), dish: c(r.C2, 'small', 5, 'C2'), variant: c(r.C1, 'bubble', 6, 'C1'), customize: c(r.C8, 'full', 20, 'C8') }; break;
    case 'addons': states = { kitchen: c(r.C4, 'small', 3, 'C4'), category: c(r.C3, 'bubble', 4, 'C3'), dish: c(r.C2, 'small', 5, 'C2'), variant: c(r.C1, 'bubble', 6, 'C1'), customize: c(r.C6, 'bubble', 7, 'C6'), addons: c(r.C8, 'full', 20, 'C8') }; break;
    case 'quantity': states = { kitchen: c(r.C4, 'bubble', 1, 'C4'), dish: c(r.C3, 'bubble', 2, 'C3') }; break;
    case 'summary': states = { kitchen: c(r.C4, 'bubble', 1, 'C4') }; break;
    default: states = {};
  }
  return { states, currentConfig: cfg };
};

interface DialOrderingFlowProps {
  kitchens?: Kitchen[];
  kitchenId?: string;
  item?: MenuItem;
  onComplete: (selection: any) => void;
  onCancel: () => void;
  dialRotation?: number;
  setDialRotation?: (val: number) => void;
  onUpdateStatus?: (status: string) => void;
  isSelectionTriggered?: boolean;
  onSelectionHandled?: () => void;
}

// --- SLOT CALCULATOR ---
const getValidSlots = (ring: string) => {
  const slots = [0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330];
  const OUTER_RINGS = ['C5', 'C6', 'C7', 'C8'];
  const isOuter = OUTER_RINGS.includes(ring);

  return slots.filter(deg => {
    // 1. SOUTH Zone (Finger Stop): Only Block 5 o'clock (60 degrees)
    if (deg === 60) return false;

    // 2. NORTH Zone (Message Board): 240, 270, 300. Blocked for Outer Rings.
    if (isOuter && (deg === 240 || deg === 270 || deg === 300)) return false;

    return true;
  });
};

const OrbitLayer = ({ items = [], config, color, activeItem, iconSizes, onSelect }: any) => {
  if (!config || config.mode === 'off' || !items.length) return null;
  
  let size = iconSizes.full;
  if (config.mode === 'small') size = iconSizes.small;
  if (config.mode === 'bubble') size = iconSizes.bubble;

  // Get valid positions for this ring
  const validSlots = getValidSlots(config.ring);
  
  // Map items to slots
  const renderItems = items.slice(0, validSlots.length).map((item: any, i: number) => {
      const angleDeg = validSlots[i];
      const angleRad = angleDeg * Math.PI / 180;
      
      return {
          item,
          angle: angleDeg,
          x: config.radius * Math.cos(angleRad),
          y: config.radius * Math.sin(angleRad),
          id: item.uid || item.id || item.name,
          label: item.kitchen_name || item.name,
          img: item.logoUrl || item.photoUrl || "/ruchira-logo.png"
      };
  });

  return (
    <div className="absolute inset-0 pointer-events-none flex items-center justify-center" style={{ zIndex: config.zIndex }}>
      {/* Visual Ring */}
      <motion.svg width={800} height={800} className="absolute overflow-visible" animate={{ opacity: config.mode === 'bubble' ? 0.3 : 0.5 }}>
        <motion.circle cx={400} cy={400} animate={{ r: config.radius }} transition={{ type: "spring", stiffness: 100, damping: 20 }} stroke={color} strokeWidth={config.mode === 'bubble' ? 1 : 1.5} fill="none" strokeDasharray={config.mode === 'bubble' ? "2 4" : "4 4"} />
      </motion.svg>
      
      {renderItems.map((node: any) => {
        const isHighlighted = activeItem && (activeItem.id === node.item.id || activeItem.uid === node.item.uid);
        const isFullMode = config.mode === 'full';

        return (
          <motion.div 
            key={node.id} 
            layoutId={`item-${node.id}`} 
            animate={{ 
                x: node.x, 
                y: node.y, 
                scale: (isFullMode && isHighlighted) ? 1.3 : 1, 
                opacity: config.mode === 'bubble' && !isHighlighted ? 0.5 : 1 
            }} 
            transition={{ type: "spring", stiffness: 150, damping: 25 }} 
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center justify-center pointer-events-auto"
            onClick={() => onSelect(node.item)}
          >
            {config.mode === 'bubble' ? (
                <div className={`rounded-full transition-all ${isHighlighted ? 'bg-white scale-150' : ''}`} style={{ width: size, height: size, backgroundColor: isHighlighted ? '#fff' : color }} />
            ) : (
              <div className="group relative flex flex-col items-center">
                 <div className={`rounded-full overflow-hidden border-2 bg-slate-900 shadow-lg transition-all relative ${isHighlighted ? 'ring-4 ring-cyan-400 shadow-[0_0_20px_rgba(34,211,238,0.6)]' : ''}`} 
                      style={{ width: size, height: size, borderColor: isHighlighted ? '#fff' : color }}>
                    <img src={node.img} alt={node.label} className="w-full h-full object-cover" onError={(e) => e.currentTarget.src="/ruchira-logo.png"} />
                 </div>
                 {(isHighlighted && isFullMode) && (
                    <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="absolute top-full mt-3 bg-black/90 text-white text-[10px] font-bold px-3 py-1 rounded-full border border-cyan-500/50 whitespace-nowrap z-50 shadow-xl tracking-widest">
                        {node.label}
                    </motion.div>
                 )}
              </div>
            )}
          </motion.div>
        );
      })}
    </div>
  );
};

export const DialOrderingFlow: React.FC<DialOrderingFlowProps> = ({ 
  kitchens, kitchenId, item, onComplete, onCancel, 
  dialRotation = 0, setDialRotation, onUpdateStatus, isSelectionTriggered, onSelectionHandled
}) => {
  const { user } = useAuth(); // Hook for User ID
  
  const initialStep = useMemo(() => {
    if (item) return (item.Variants && item.Variants.length > 0) ? 'variant' : 'customize';
    if (kitchenId) return 'category';
    return 'kitchen';
  }, [item, kitchenId]);

  const [step, setStep] = useState<Step>(initialStep);
  const [selectedKitchen, setSelectedKitchen] = useState<Kitchen | null>(kitchens?.find(k => k.uid === kitchenId || k.id === kitchenId) || null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(item?.category || null);
  const [selectedDish, setSelectedDish] = useState<MenuItem | null>(item || null);
  const [selectedVariant, setSelectedVariant] = useState<any>(item?.Variants?.[0] || null);
  const [selectedCustomizations, setSelectedCustomizations] = useState<string[]>([]);
  const [selectedAddons, setSelectedAddons] = useState<string[]>([]);
  const [quantity, setQuantity] = useState(1);

  const activeKitchenId = selectedKitchen?.uid || kitchenId || item?.kitchenId;
  const { data: menuData } = useQuery<Menu>(["menu", activeKitchenId], () => getMenuByKitchenId(activeKitchenId!), { enabled: !!activeKitchenId });

  // Data
  const categories = useMemo(() => { if (!menuData?.menuItems) return []; const m = new Map(); Object.values(menuData.menuItems).forEach((it: any) => { const n = it.category || "General"; if (!m.has(n)) m.set(n, { id: n, name: n, image: it.photoUrl }); }); return Array.from(m.values()); }, [menuData]);
  const dishes = useMemo(() => { if (!menuData?.menuItems || !selectedCategory) return []; return Object.values(menuData.menuItems).filter((it: any) => (it.category || "General") === selectedCategory) as MenuItem[]; }, [menuData, selectedCategory]);
  const MOCK_CUSTOMS = [{id: 'spicy', name: 'Spicy'}, {id: 'oil', name: 'Less Oil'}, {id: 'salt', name: 'No Salt'}, {id: 'x', name: 'Xtra Sauce'}];
  
  // --- CONFIG ---
  const { states: configs, currentConfig } = getOrbitConfig(step);
  
  // --- POINTER LOGIC ---
  const activeKey = Object.keys(configs).find(k => configs[k].mode === 'full');
  
  let currentItems: any[] = [];
  if (activeKey === 'kitchen') currentItems = kitchens || [];
  else if (activeKey === 'category') currentItems = categories;
  else if (activeKey === 'dish') currentItems = dishes;
  else if (activeKey === 'variant') currentItems = selectedDish?.Variants || [];
  else if (activeKey === 'customize') currentItems = MOCK_CUSTOMS;
  else if (activeKey === 'addons') currentItems = selectedDish?.Addons || [];

  const activeRingId = activeKey ? configs[activeKey].ring : 'C1';
  const validSlots = getValidSlots(activeRingId);
  const displayItems = currentItems.slice(0, validSlots.length); 

  // Map Dial Rotation (0=Top) to Closest Slot (0=Right)
  // Target Slot Angle = (DialRotation - 90)
  const targetAngle = (dialRotation - 90 + 360) % 360; 
  
  let closestIndex = -1;
  let minDiff = 1000;

  validSlots.forEach((slotAngle, idx) => {
      let diff = Math.abs(slotAngle - targetAngle);
      if (diff > 180) diff = 360 - diff;
      
      if (diff < minDiff) {
          minDiff = diff;
          closestIndex = idx;
      }
  });

  const activeItem = closestIndex !== -1 ? displayItems[closestIndex] : null;

  // --- SELECTION HANDLER ---
  useEffect(() => {
      if (isSelectionTriggered && activeItem) {
          handleSelection(activeItem);
          if (onSelectionHandled) onSelectionHandled();
          if (setDialRotation) setDialRotation(0); 
      }
  }, [isSelectionTriggered]);

  const handleSelection = (selection: any) => {
      switch(step) {
          case 'kitchen': setSelectedKitchen(selection); setTimeout(() => setStep('category'), 200); break;
          case 'category': setSelectedCategory(selection.name); setTimeout(() => setStep('dish'), 200); break;
          case 'dish': 
              setSelectedDish(selection); 
              if (selection.Variants?.length) setTimeout(() => setStep('variant'), 200);
              else setTimeout(() => setStep('customize'), 200);
              break;
          case 'variant': setSelectedVariant(selection); setTimeout(() => setStep('customize'), 200); break;
          case 'customize': 
              setSelectedCustomizations(p => p.includes(selection.name) ? p.filter(x => x !== selection.name) : [...p, selection.name]); 
              break;
          case 'addons': 
              setSelectedAddons(p => p.includes(selection.name) ? p.filter(x => x !== selection.name) : [...p, selection.name]); 
              break;
      }
  };

  // --- SAVE PREFERENCE LOGIC ---
  const handleSavePreference = async (type: 'subscription' | 'favorite') => {
    if (!user) return toast.error("Please login to save");
    
    // Construct preference object
    const prefData: SavedPreference = {
        id: crypto.randomUUID(),
        type,
        createdAt: new Date().toISOString(),
        kitchenId: activeKitchenId || "",
        kitchenName: selectedKitchen?.kitchen_name || "Kitchen",
        dishId: selectedDish?.id || "",
        dishName: selectedDish?.name || "",
        photoUrl: selectedDish?.photoUrl,
        config: {
            variant: selectedVariant?.name,
            customizations: selectedCustomizations,
            addons: selectedAddons,
            quantity: quantity,
            price: (selectedVariant?.price || selectedDish?.price || 0) * quantity
        }
    };

    await saveUserPreference(user.uid, prefData);
  };

  // --- QUANTITY LOGIC ---
  const prevRotation = useRef(dialRotation);
  const rotationAccumulator = useRef(0);
  useEffect(() => {
    if (step === 'quantity') {
        const delta = dialRotation - prevRotation.current;
        if (Math.abs(delta) < 100) {
            rotationAccumulator.current += delta;
            if (rotationAccumulator.current > 30) { setQuantity(q => Math.min(50, q + 1)); rotationAccumulator.current = 0; }
            else if (rotationAccumulator.current < -30) { setQuantity(q => Math.max(1, q - 1)); rotationAccumulator.current = 0; }
        }
    }
    prevRotation.current = dialRotation;
  }, [dialRotation, step]);

  // Status Updater
  useEffect(() => {
    if (onUpdateStatus) {
        let msg = "";
        if (step === 'quantity') msg = `QTY: ${quantity}`;
        else if (activeItem) msg = (activeItem.kitchen_name || activeItem.name || "").toUpperCase();
        else msg = step.toUpperCase();
        onUpdateStatus(msg);
    }
  }, [step, activeItem, quantity, onUpdateStatus]);

  const offset = currentConfig.offset;
  const iconSizes = currentConfig.iconSizes;

  const goBack = () => {
      const idx = ['kitchen', 'category', 'dish', 'variant', 'customize', 'addons', 'quantity', 'summary'].indexOf(step);
      if (idx === 0) onCancel();
      else {
          setStep(prev => {
             const order = ['kitchen', 'category', 'dish', 'variant', 'customize', 'addons', 'quantity', 'summary'];
             return order[idx - 1] as Step;
          });
          if (setDialRotation) setDialRotation(0);
      }
  };
  const goNext = () => {
      if (step === 'summary') {
          onComplete({ kitchenId: activeKitchenId, dish: selectedDish, variant: selectedVariant?.name, quantity, price: (selectedVariant?.price || selectedDish?.price || 0) * quantity, customizations: selectedCustomizations, addons: selectedAddons });
      } else {
          const order = ['kitchen', 'category', 'dish', 'variant', 'customize', 'addons', 'quantity', 'summary'];
          const idx = order.indexOf(step);
          setStep(order[idx + 1] as Step);
          if (setDialRotation) setDialRotation(0);
      }
  };

  return (
    <div className="absolute inset-0 pointer-events-none">
        {/* CONTAINER - STATIC */}
        <div className="absolute inset-0 z-30 flex items-center justify-center transition-transform duration-300" style={{ transform: `translate(${offset.x}px, ${offset.y}px)` }}>
            {configs.kitchen && <OrbitLayer items={kitchens || []} config={configs.kitchen} color="#06b6d4" activeItem={activeKey === 'kitchen' ? activeItem : null} onSelect={handleSelection} iconSizes={iconSizes} />}
            {configs.category && <OrbitLayer items={categories} config={configs.category} color="#3b82f6" activeItem={activeKey === 'category' ? activeItem : null} onSelect={handleSelection} iconSizes={iconSizes} />}
            {configs.dish && <OrbitLayer items={dishes} config={configs.dish} color="#06b6d4" activeItem={activeKey === 'dish' ? activeItem : null} onSelect={handleSelection} iconSizes={iconSizes} />}
            
            {configs.variant && <OrbitLayer items={selectedDish?.Variants || []} config={configs.variant} color="#a855f7" activeItem={activeKey === 'variant' ? activeItem : null} onSelect={handleSelection} iconSizes={iconSizes} />}
            {configs.customize && <OrbitLayer items={MOCK_CUSTOMS} config={configs.customize} color="#ec4899" activeItem={activeKey === 'customize' ? activeItem : null} onSelect={handleSelection} iconSizes={iconSizes} />}
            {configs.addons && <OrbitLayer items={selectedDish?.Addons || []} config={configs.addons} color="#f43f5e" activeItem={activeKey === 'addons' ? activeItem : null} onSelect={handleSelection} iconSizes={iconSizes} />}
            
            {step === 'quantity' && <div className="z-50 text-center mb-12 drop-shadow-2xl"><span className="text-8xl font-mono font-bold text-white">{quantity}</span></div>}
            
            {step === 'summary' && (
                <div className="absolute inset-0 flex items-center justify-center z-50 pointer-events-auto backdrop-blur-md flex-col">
                    <div className="bg-slate-900/95 p-8 rounded-2xl border border-cyan-500/50 text-center shadow-2xl max-w-xs mb-4">
                        <h2 className="text-xl font-bold text-white mb-1 uppercase">{selectedDish?.name}</h2>
                        <p className="text-cyan-400 text-sm mb-4 font-mono">{selectedVariant?.name || "Standard"}</p>
                        <div className="text-5xl font-mono font-bold text-green-400 mb-2">₹{(selectedVariant?.price || selectedDish?.price || 0) * quantity}</div>
                    </div>
                    
                    {/* SAVE BUTTONS */}
                    <div className="flex gap-4">
                        <button onClick={() => handleSavePreference('favorite')} className="bg-pink-600/90 text-white px-4 py-2 rounded-full font-bold text-xs border border-pink-400 hover:bg-pink-500 transition-colors">♥ FAVORITE</button>
                        <button onClick={() => handleSavePreference('subscription')} className="bg-purple-600/90 text-white px-4 py-2 rounded-full font-bold text-xs border border-purple-400 hover:bg-purple-500 transition-colors">📅 SUBSCRIBE</button>
                    </div>
                </div>
            )}
        </div>

        {/* BUTTONS */}
        <div className="absolute z-50 pointer-events-auto cursor-pointer" style={{ bottom: BUTTON_POS.bottom, left: BUTTON_POS.side }} onClick={goBack}>
            <div className="w-24 h-24 relative flex items-center justify-center cursor-pointer group hover:scale-105 transition-all">
                <img src="/images/buttonbg.png" alt="Back" className="absolute inset-0 w-full h-full object-contain opacity-90" />
                <span className="relative z-10 font-bold text-[10px] uppercase tracking-widest text-red-400">BACK</span>
            </div>
        </div>

        <div className="absolute z-50 pointer-events-auto cursor-pointer" style={{ bottom: BUTTON_POS.bottom, right: BUTTON_POS.side }} onClick={goNext}>
            <div className="w-24 h-24 relative flex items-center justify-center cursor-pointer group hover:scale-105 transition-all">
                <img src="/images/buttonbg.png" alt="OK" className="absolute inset-0 w-full h-full object-contain opacity-90" />
                <span className="relative z-10 font-bold text-[10px] uppercase tracking-widest text-green-400">{step === 'summary' ? 'DONE' : 'OK'}</span>
            </div>
        </div>
    </div>
  );
};